import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowUpRight, Check, ChevronsUpDown, CheckCircle2, Loader2,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import ShareButtons from "../components/ShareButtons";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { COUNTRIES, ROLES, INTERESTS, MEMBERSHIP_PREFS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CountryCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="join-country-trigger"
          className="flex h-11 w-full items-center justify-between rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] px-3.5 text-sm text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)]"
        >
          <span className={value ? "" : "text-[color:var(--ink-soft)]"}>
            {value || "Select your country"}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." data-testid="join-country-search" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  data-testid={`join-country-option-${c}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c ? "opacity-100" : "opacity-0"}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const INITIAL = {
  name: "",
  email: "",
  country: "",
  role: "",
  business_name: "",
  interests: [],
  preferred_membership: "",
};

export default function Join() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleInterest = (interest) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.country) return "Please select your country.";
    if (!form.role) return "Please select your role.";
    if (!form.preferred_membership) return "Please select a preferred membership.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error("This email is already on the early-access list.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Join Hub"
        description="Join the StUpEco Hub early-access list. Register your interest to be first when the business growth network launches."
        path="/join"
      />
      <section className="relative min-h-screen pt-32 pb-24 lg:pt-40" data-testid="join-page">
        <div className="grid-lines absolute inset-0 -z-10 opacity-40" />
        <div className="container-hub">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto max-w-xl text-center"
                data-testid="join-success"
              >
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:var(--brand-green)]/12 text-[color:var(--brand-green)]">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h1 className="font-display mt-8 text-4xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-5xl">
                  You're on the list.
                </h1>
                <p className="mt-5 text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">
                  Thank you for joining the StUpEco Hub early-access list. We've
                  received your registration and sent a confirmation to your
                  email. We'll share updates as the Hub develops.
                </p>
                <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                  <Link to="/" data-testid="join-success-home" className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                    Back to Home
                  </Link>
                  <Link to="/features" data-testid="join-success-features" className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-7 py-4 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--ink)]">
                    Explore Features
                  </Link>
                </div>
                <div className="mt-12 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-6">
                  <p className="text-sm font-medium text-[color:var(--ink)]">
                    Know another founder, business owner, or professional who would benefit from StUpEco Hub?
                  </p>
                  <p className="mt-1 text-sm text-[color:var(--ink-soft)]">Share the Hub with your network.</p>
                  <div className="mt-4 flex justify-center">
                    <ShareButtons
                      url={`${window.location.origin}/join`}
                      title="Join StUpEco Hub — the business growth network"
                      testId="join-share"
                    />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16"
              >
                <div className="lg:col-span-5">
                  <Reveal>
                    <span className="eyebrow">
                      <span className="brand-bar h-1.5 w-1.5 rounded-full" />
                      Join Hub · Early access
                    </span>
                  </Reveal>
                  <Reveal delay={0.05}>
                    <h1 className="font-display mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl">
                      Grow together with the Hub.
                    </h1>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <p className="mt-6 text-base leading-relaxed text-[color:var(--ink-soft)]">
                      Register your interest to join the StUpEco Hub early-access
                      list. This records your interest — it does not yet create a
                      member account. We'll send a confirmation and keep you
                      posted as the Hub develops.
                    </p>
                  </Reveal>
                  <Reveal delay={0.15}>
                    <div className="mt-8 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]">
                        What happens next
                      </p>
                      <ul className="mt-4 space-y-3 text-sm text-[color:var(--ink)]">
                        {["We confirm your early-access registration by email", "You receive updates as features go live", "You're first in line when member logins open"].map((t) => (
                          <li key={t} className="flex items-start gap-2.5">
                            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--brand-teal)]" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                </div>

                <div className="lg:col-span-7">
                  <Reveal delay={0.1}>
                    <form
                      onSubmit={onSubmit}
                      data-testid="join-form"
                      className="rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-7 lg:p-10"
                      noValidate
                    >
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full name <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Input id="name" data-testid="join-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="h-11 rounded-xl" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Input id="email" type="email" data-testid="join-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" className="h-11 rounded-xl" />
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Country <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <CountryCombobox value={form.country} onChange={(v) => set("country", v)} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="business">Business name</Label>
                          <Input id="business" data-testid="join-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" />
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Role <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Select value={form.role} onValueChange={(v) => set("role", v)}>
                            <SelectTrigger data-testid="join-role-trigger" className="h-11 rounded-xl">
                              <SelectValue placeholder="Select your role" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {ROLES.map((r) => (
                                <SelectItem key={r} value={r} data-testid={`join-role-option-${r}`}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred membership <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Select value={form.preferred_membership} onValueChange={(v) => set("preferred_membership", v)}>
                            <SelectTrigger data-testid="join-membership-trigger" className="h-11 rounded-xl">
                              <SelectValue placeholder="Select a plan interest" />
                            </SelectTrigger>
                            <SelectContent>
                              {MEMBERSHIP_PREFS.map((m) => (
                                <SelectItem key={m} value={m} data-testid={`join-membership-option-${m}`}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mt-7 space-y-3">
                        <Label>Primary interest <span className="font-normal text-[color:var(--ink-soft)]">(select all that apply)</span></Label>
                        <div className="flex flex-wrap gap-2" data-testid="join-interests">
                          {INTERESTS.map((interest) => {
                            const selected = form.interests.includes(interest);
                            return (
                              <button
                                type="button"
                                key={interest}
                                data-testid={`join-interest-${interest}`}
                                onClick={() => toggleInterest(interest)}
                                aria-pressed={selected}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                                  selected
                                    ? "border-[color:var(--ink)] bg-[color:var(--invert-bg)] text-white"
                                    : "border-[color:var(--hairline)] bg-[color:var(--surface)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
                                }`}
                              >
                                {selected && <Check className="h-3.5 w-3.5" />}
                                {interest}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="join-submit"
                        className="group mt-9 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                          </>
                        ) : (
                          <>
                            Join the early-access list
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </>
                        )}
                      </button>
                      <p className="mt-4 text-center text-xs text-[color:var(--ink-soft)]">
                        By joining you agree to receive early-access updates about StUpEco Hub.
                      </p>
                    </form>
                  </Reveal>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  );
}
